import { createStore, type StoreApi } from "zustand";

/** Slots pages claim to replace the shell's own content. */
export type ShellSlot = "topBarLeading" | "topBarTrailing" | "bottomNavAction" | "bottomNavTabRow";

/** Regions the shell mounts a portal host for: the claimable slots plus the overlay layer. */
export type ShellRegion = ShellSlot | "overlay";

export interface ShellSlotState {
  /** Id of the claim rendering into the region, or null while the shell's fallback shows. */
  ownerId: string | null;
  /** Element claims portal into, or null while the region's host is unmounted. */
  host: HTMLElement | null;
  visibleDuringOverlay: boolean;
}

export interface ShellState {
  regions: Record<ShellRegion, ShellSlotState>;
}

export interface ShellChrome {
  store: StoreApi<ShellState>;
  /** Publishes `id` into the slot until the returned release runs. Claims stack: the latest
   *  unreleased claim owns the slot. Releasing a buried claim changes nothing; releasing the top
   *  one hands the slot to the claim beneath. */
  claim(slot: ShellSlot, id: string, options?: { visibleDuringOverlay?: boolean }): () => void;
  openOverlay(id: string, dismiss: () => void): () => void;
  requestDismiss(): boolean;
  /** Registers the region's portal target until the returned release runs. */
  mountHost(region: ShellRegion, host: HTMLElement | null): () => void;
}

const UNCLAIMED: ShellSlotState = { ownerId: null, host: null, visibleDuringOverlay: false };

interface Claim {
  id: string;
  visibleDuringOverlay: boolean;
}

const initialState = (): ShellState => ({
  regions: {
    topBarLeading: UNCLAIMED,
    topBarTrailing: UNCLAIMED,
    bottomNavAction: UNCLAIMED,
    bottomNavTabRow: UNCLAIMED,
    overlay: UNCLAIMED,
  },
});

export function createShellChrome(): ShellChrome {
  const store = createStore<ShellState>(initialState);
  const claims = new Map<ShellRegion, Claim[]>();
  let dismissOverlay: (() => void) | null = null;

  const regionOf = (region: ShellRegion) => store.getState().regions[region];

  const patch = (region: ShellRegion, next: Partial<ShellSlotState>) =>
    store.setState((state) => ({
      regions: { ...state.regions, [region]: { ...state.regions[region], ...next } },
    }));

  const publishTop = (region: ShellRegion) => {
    const top = claims.get(region)?.at(-1);
    patch(region, { ownerId: top?.id ?? null, visibleDuringOverlay: top?.visibleDuringOverlay ?? false });
  };

  const claimRegion = (region: ShellRegion, id: string, visibleDuringOverlay = false) => {
    const claim: Claim = { id, visibleDuringOverlay };
    claims.set(region, [...(claims.get(region) ?? []), claim]);
    publishTop(region);

    return () => {
      claims.set(
        region,
        (claims.get(region) ?? []).filter((held) => held !== claim),
      );
      publishTop(region);
    };
  };

  return {
    store,
    claim: (slot, id, options) => claimRegion(slot, id, options?.visibleDuringOverlay),

    openOverlay(id, dismiss) {
      const open = regionOf("overlay").ownerId;
      if (open !== null && open !== id)
        throw new Error(
          `Overlay "${id}" opened while "${open}" is open. Only one can be open at a time, so route away instead of layering a second one.`,
        );

      dismissOverlay = dismiss;
      const release = claimRegion("overlay", id);

      return () => {
        if (dismissOverlay === dismiss) dismissOverlay = null;
        release();
      };
    },

    requestDismiss() {
      if (!dismissOverlay) return false;

      dismissOverlay();
      return true;
    },

    mountHost(region, host) {
      patch(region, { host });

      return () => {
        if (regionOf(region).host !== host) return;

        patch(region, { host: null });
      };
    },
  };
}

export const NOOP_SHELL_CHROME: ShellChrome = {
  store: createStore<ShellState>(initialState),
  claim: () => () => undefined,
  openOverlay: () => () => undefined,
  requestDismiss: () => false,
  mountHost: () => () => undefined,
};
